import CryptoJS from 'crypto-js/core.js';
import 'crypto-js/sha256.js';
import 'crypto-js/lib-typedarrays.js';

const TRANSPORT = 'academic-pdf-chunks-v1';
const MAX_CHUNK_BYTES = 1024 * 1024;
const invalidPdf = () => Object.assign(new Error('The PDF transfer was incomplete or returned an invalid file. Please retry.'), { code: 'ACADEMIC_PDF_RESPONSE_INVALID' });
const cancelled = (error, signal) => signal?.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'AbortError';
const checkCancelled = signal => { if (signal?.aborted) throw Object.assign(new Error('PDF transfer cancelled.'), { code: 'ERR_CANCELED' }); };
const sha = bytes => CryptoJS.SHA256(CryptoJS.lib.WordArray.create(bytes)).toString();
const hex = value => /^[a-f0-9]{64}$/.test(value || '');
const reset = state => { for (const key of Object.keys(state)) delete state[key]; };
const wait = (ms, signal) => new Promise((resolve, reject) => {
  checkCancelled(signal);
  const done = () => { signal?.removeEventListener('abort', stop); resolve(); };
  const timer = setTimeout(done, ms);
  const stop = () => { clearTimeout(timer); signal?.removeEventListener('abort', stop); reject(Object.assign(new Error('PDF transfer cancelled.'), { code: 'ERR_CANCELED' })); };
  signal?.addEventListener('abort', stop, { once: true });
});
async function checked(blob, expectedBytes) {
  const size = Number(expectedBytes);
  // Upload limits do not apply to viewing an already-saved PDF.
  if (!(blob instanceof Blob) || blob.size < 5 ||
    (Number.isSafeInteger(size) && size > 0 && blob.size !== size) || await blob.slice(0, 5).text() !== '%PDF-') throw invalidPdf();
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
}
function decode(data, length) {
  if (typeof data !== 'string' || data.length !== 4 * Math.ceil(length / 3) || !/^[A-Za-z0-9+/]*={0,2}$/.test(data)) throw invalidPdf();
  let raw; try { raw = atob(data); } catch { throw invalidPdf(); }
  if (raw.length !== length) throw invalidPdf();
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}
async function request(api, path, options, onRetry) {
  for (let attempt = 0; ; attempt++) {
    checkCancelled(options.signal);
    try { return await api.get(path, { ...options, retryOnNetworkError: false, networkRetryAttempts: 0 }); }
    catch (error) {
      const status = Number(error?.response?.status || error?.status || 0);
      if (attempt >= 2 || cancelled(error, options.signal) || (status && ![408, 429, 502, 503, 504].includes(status)) || error?.code === 'ACADEMIC_RESPONSE_INVALID') throw error;
      onRetry?.();
      const retryAfter = Number(error.response?.headers?.['retry-after']);
      await wait(Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter * 1000, 5000) : 250 * (attempt + 1), options.signal);
    }
  }
}
async function legacyPdf(api, path, { params, signal, expectedBytes, onProgress, fallback }) {
  const progress = event => onProgress?.({ phase: 'receiving', percent: event.total ? Math.min(100, Math.round(event.loaded / event.total * 100)) : null, loaded: event.loaded, total: event.total });
  try {
    const response = await api.get(path, { params, responseType: 'blob', timeout: 180000, signal, networkRetryAttempts: 1, onDownloadProgress: progress });
    return await checked(response.data, expectedBytes);
  } catch (error) {
    const status = error?.response?.status || error?.status;
    if (!fallback || cancelled(error, signal) || error.code === 'ACADEMIC_PDF_RESPONSE_INVALID' || (status && ![408, 502, 503, 504].includes(status))) throw error;
    onProgress?.({ phase: 'reconnecting', percent: null });
    const { data: body } = await api.get(path, { params: { ...params, format: 'json' }, timeout: 180000, signal, networkRetryAttempts: 0, onDownloadProgress: progress });
    if (body?.mimeType !== 'application/pdf' || !Number.isSafeInteger(body.bytes) || body.bytes < 5 || !hex(body.sha256)) throw invalidPdf();
    const bytes = decode(body.data, body.bytes);
    if (sha(bytes) !== body.sha256) throw invalidPdf();
    return checked(new Blob([bytes], { type: 'application/pdf' }), expectedBytes ?? body.bytes);
  }
}
export async function readProtectedPdf(api, path, { expectedBytes, signal, onProgress, params, fallback = true, resume = {} } = {}) {
  try {
    onProgress?.({ phase: 'preparing', percent: null });
    // Generated transcripts retain their binary path; stored chat/assessment
    // PDFs use small authenticated JSON responses from the first request.
    if (!fallback) return await legacyPdf(api, path, { params, signal, expectedBytes, onProgress, fallback });
    const reconnecting = () => onProgress?.({ phase: 'reconnecting', percent: resume.manifest ? Math.floor((resume.offset || 0) / resume.manifest.bytes * 100) : null,
      loaded: resume.offset || 0, total: resume.manifest?.bytes });
    const response = await request(api, path, { params: { ...params, format: 'manifest' }, timeout: 30000, signal }, reconnecting);
    const manifest = response.data;
    // Rolling deployment compatibility only when an old Core clearly returned
    // a PDF. Permission errors and malformed HTML/JSON never change routes.
    if ((manifest instanceof Blob && await manifest.slice(0, 5).text() === '%PDF-') ||
        (typeof manifest === 'string' && manifest.startsWith('%PDF-')))
      return await legacyPdf(api, path, { params, signal, expectedBytes, onProgress, fallback });
    if (manifest?.transport !== TRANSPORT || manifest.mimeType !== 'application/pdf' ||
        !Number.isSafeInteger(manifest.bytes) || manifest.bytes < 5 || !hex(manifest.sha256) ||
        !Number.isSafeInteger(manifest.chunkBytes) || manifest.chunkBytes < 5 || manifest.chunkBytes > MAX_CHUNK_BYTES ||
        (expectedBytes != null && Number(expectedBytes) !== manifest.bytes)) throw invalidPdf();
    const key = JSON.stringify([path, params || {}, manifest.bytes, manifest.sha256]);
    if (resume.key !== key) { reset(resume); Object.assign(resume, { key, manifest, offset: 0, parts: [], digest: CryptoJS.algo.SHA256.create() }); }
    onProgress?.({ phase: 'receiving', percent: Math.floor(resume.offset / manifest.bytes * 100), loaded: resume.offset, total: manifest.bytes });
    while (resume.offset < manifest.bytes) {
      checkCancelled(signal);
      const offset = resume.offset, length = Math.min(manifest.chunkBytes, manifest.bytes - offset);
      const { data: part } = await request(api, path, { params: { ...params, format: 'chunk', offset, length, sha256: manifest.sha256 }, timeout: 60000, signal }, reconnecting);
      checkCancelled(signal);
      if (part?.transport !== TRANSPORT || part.mimeType !== 'application/pdf' || part.bytes !== manifest.bytes || part.sha256 !== manifest.sha256 ||
          part.offset !== offset || part.length !== length || !hex(part.chunkSha256)) throw invalidPdf();
      const bytes = decode(part.data, length);
      if (sha(bytes) !== part.chunkSha256 || (offset === 0 && String.fromCharCode(...bytes.subarray(0, 5)) !== '%PDF-')) throw invalidPdf();
      resume.digest.update(CryptoJS.lib.WordArray.create(bytes));
      resume.parts.push(new Blob([bytes])); resume.offset += length;
      onProgress?.({ phase: 'receiving', percent: Math.floor(resume.offset / manifest.bytes * 100), loaded: resume.offset, total: manifest.bytes });
    }
    onProgress?.({ phase: 'verifying', percent: 100, loaded: manifest.bytes, total: manifest.bytes });
    if (resume.digest.clone().finalize().toString() !== manifest.sha256) { reset(resume); throw invalidPdf(); }
    const blob = await checked(new Blob(resume.parts, { type: 'application/pdf' }), manifest.bytes);
    reset(resume); return blob;
  } catch (error) {
    if (cancelled(error, signal) || error?.code === 'ACADEMIC_PDF_RESPONSE_INVALID') throw error;
    let data = error?.response?.data;
    if (data instanceof Blob && data.size <= 64 * 1024) { try { data = JSON.parse(await data.text()); } catch { data = null; } }
    const serverMessage = typeof data?.error === 'string' ? data.error : typeof data?.message === 'string' ? data.message : '';
    throw Object.assign(new Error(serverMessage.slice(0, 240) || (error?.response
      ? 'PDF could not be opened. Please retry.' : 'The PDF connection was interrupted. Retry PDF to continue. If this continues, contact the office.')),
    { code: data?.code || 'ACADEMIC_PDF_TRANSFER_FAILED', cause: error });
  }
}
export const readAcademicPdf = (api, messageId, attachmentId, options) => readProtectedPdf(api,
  `/academic-chat/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`, options);
